import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import Select from "react-select";
import { roleTypes } from "../../../data/roleType";

const PhaseAdd = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [systems, setSystems] = useState([]);
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const initialFormData = { name: "", code: "", systemId: null };

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const config = {
          headers: { Authorization: `${token}` },
        };
        const response = await axios.get(`${url.backendURL}system/all`, config);
        setSystems(response.data.response.data);
        if (alertRef.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }

        setErrors({});
      } catch (err) {
        handleError(err, "phase-add");
      }
    };
    fetchData();
  }, [decodedToken]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const config = {
        headers: { Authorization: `${token}` },
      };
      const response = await axios.post(
        `${url.backendURL}phase/`,
        values,
        config
      );

      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response.data.message,
        type: "success",
        show: true,
        alertFor: "phase-add",
      });

      setErrors({});
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
        navigate(`/dashboard/app/phase-list`);
      }, 2000);
    } catch (err) {
      handleError(err, "phase-add");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response.data.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "Phase name must contain at least one letter or number"
      )
      .min(1, "Phase name must be at least 1 character long")
      .max(255, "Phase name must not exceed 255 characters")
      .trim()
      .required("Phase Name is required"),
    code: Yup.string()
      .min(1, "Code must be at least 1 character long")
      .max(20, "Code must not exceed 20 characters")
      .trim()
      .required("Code is required"),
    systemId: Yup.number()
      .integer()
      .positive()
      .required("System is required")
      .typeError("System must be a valid"),
  });

  const handleReset = (resetForm) => {
    resetForm();
    setErrors({});
  };

  const [options, setOptions] = useState([]);

  useEffect(() => {
    const systemOptions = [];

    if (systems.length > 0) {
      const mappedOptions = systems?.map((system) => ({
        value: system.id,
        label: system.name,
      }));
      systemOptions.push(...mappedOptions);
    }

    setOptions(systemOptions);
  }, [systems]);

  return (
    <>
      <Formik
        initialValues={initialFormData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          resetForm,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit}>
            <FocusError />
            <Row>
              <Col md="12" ref={alertRef}>
                <Card>
                  <Card.Header className="d-flex justify-content-between">
                    <div
                      className="breadcrumb header-title"
                      style={{ cursor: "pointer" }}
                    >
                      <h4
                        onClick={() => navigate("/dashboard/app/phase-list")}
                        className="text-primary card-title"
                        style={{ cursor: "pointer" }}
                      >
                        <span>Phase</span>
                      </h4>
                      <h4 className="mx-2 card-title">{">"}</h4>
                      <h4 className="card-title">Add</h4>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <AlertNotificationSimple
                      type={alert.type}
                      message={alert.message}
                      show={alert.show}
                      onClose={() => setAlert({ ...alert, show: false })}
                    />
                    <div className="new-phase-info">
                      <Row>
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="name"
                          placeholder="Enter name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.name}
                          label="Name"
                        />
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="code"
                          placeholder="Enter code"
                          value={values.code}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.code}
                          label="Code"
                        />
                      </Row>
                      <Form.Group className="col-md-6 form-group">
                        <Form.Label>
                          System<span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          closeMenuOnScroll={(e) => {
                            try {
                              const target = e.target;

                              if (
                                !target ||
                                !target.nodeType ||
                                typeof target.closest !== "function"
                              ) {
                                return true;
                              }

                              const isMenuScroll =
                                target.closest('[class*="MenuList"]') ||
                                target.closest('[class*="menu"]');

                              return !isMenuScroll;
                            } catch (error) {
                              return true;
                            }
                          }}
                          options={options || []}
                          as="select"
                          name="systemId"
                          id="systemId"
                          data-style="py-0"
                          value={
                            options && options.length > 0
                              ? options.find(
                                  (option) => option.value === values.systemId
                                )
                              : null
                          }
                          onChange={(option) =>
                            setFieldValue(
                              "systemId",
                              option ? option.value : ""
                            )
                          }
                          isClearable
                          onBlur={handleBlur}
                          placeholder="Select a System..."
                          isSearchable
                          menuPortalTarget={document.body} // render menu in portal
                          menuPosition="fixed" // position relative to viewport
                          menuPlacement="auto"
                          styles={{
                            menu: (provided) => ({
                              ...provided,
                              maxHeight: "200px",
                              overflowY: "auto",
                            }),
                            menuList: (provided) => ({
                              ...provided,
                              maxHeight: "200px",
                              overflowY: "auto",
                            }),
                            control: (provided) => ({
                              ...provided,
                              color: "#5e667b",
                            }),
                          }}
                        />

                        <ErrorMessage
                          name="systemId"
                          component="div"
                          className="text-danger"
                        />
                        {errors?.systemId?.map((error, index) => (
                          <span
                            key={index}
                            className="w-100 mt-1 small text-danger"
                          >
                            {error}
                            <br />
                          </span>
                        ))}
                      </Form.Group>
                      <hr />
                      <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                        <Button
                          type="button"
                          className="me-2 me-md-5 px-3 px-md-4"
                          variant="btn btn-light"
                          onClick={() => handleReset(resetForm)} // Reset the form
                        >
                          Reset
                        </Button>

                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          variant="btn btn-primary"
                          className="me-2 me-md-5 px-3 px-md-4"
                        >
                          Submit
                        </Button>
                        <Button
                          type="button"
                          className=" px-3 px-md-4"
                          variant="btn btn-danger"
                          onClick={() => navigate("/dashboard/app/phase-list")}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PhaseAdd;
